import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Facebook,
  Twitter,
  Linkedin,
  ArrowRight,
  Check,
  Sparkles,
  Lock,
} from "lucide-react";
import { useApi } from "../../hooks/useApi";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";
import Footer from "../../components/Footer/Footer";
import { getFreshS3Url } from "../../utils/s3Image";
import { BLOG_SEO_OVERRIDES } from "./blogSEOOverrides";
import SolutionsFAQ from "../../components/SolutionsFAQ/SolutionsFAQ";
import "./Blog.css";

const TEAL = "#00D1C1";
const CATEGORY_COLORS = {
  "AI & Automation": "#F43F5E",
  Productivity: "#00D1C1",
  "Remote Work": "#115E59",
  "Team Management": "#6C59A6",
  Management: "#366EB6",
  "Tech Trends": "#6C59A6",
};

const SITE_BASE = "https://teamgrid.ai";
const OG_IMAGE_FALLBACK = "https://teamgrid.ai/og/og-home.webp";

const BlogDetail = () => {
  const { slug } = useParams();
  const api = useApi();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featuredImageError, setFeaturedImageError] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  useEffect(() => {
    setFeaturedImageError(false);
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/v1/blogs/${slug}`);
      const blogData = response.data.data.blog;
      if (blogData.featuredImage) {
        const freshUrl = await getFreshS3Url(blogData.featuredImage);
        blogData.featuredImage = freshUrl || blogData.featuredImage;
      }
      setBlog(blogData);
      if (blogData.category) {
        const relRes = await api.get(`/api/v1/blogs`, {
          params: { category: blogData.category, limit: 6 },
        });
        const related = relRes.data.data.blogs
          .filter((b) => b._id !== blogData._id)
          .slice(0, 3);
        const withUrls = await Promise.all(
          related.map(async (b) => {
            if (b.featuredImage) {
              const url = await getFreshS3Url(b.featuredImage);
              return { ...b, featuredImage: url || b.featuredImage };
            }
            return b;
          })
        );
        setRelatedBlogs(withUrls);
      }
    } catch (err) {
      console.error("Error fetching blog:", err);
      setError(err.response?.data?.error?.message || "Blog not found");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareToTwitter = () =>
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(blog?.title || "")}`, "_blank");
  const shareToLinkedIn = () =>
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank");
  const shareToFacebook = () =>
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank");

  if (loading) {
    return (
      <div className="blog-container bg-white min-h-screen">
        <Helmet>
          <title>Loading... | TeamGrid Blog</title>
        </Helmet>
        <PublicHeader />
        <div className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center items-center min-h-[60vh]">
          <div className="w-12 h-12 border-2 border-[#00D1C1] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="blog-container bg-white min-h-screen">
        <Helmet>
          <title>Blog Not Found | TeamGrid Blog</title>
        </Helmet>
        <PublicHeader />
        <div className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-2xl font-bold text-black mb-4">Blog Not Found</h1>
          <p className="text-[#475569] mb-6">{error || "The blog post you're looking for doesn't exist."}</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-black"
            style={{ background: TEAL }}
          >
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const categoryColor = CATEGORY_COLORS[blog.category] || "#6C59A6";

  const getInitials = (name) => {
    if (!name) return "TG";
    if (name.toLowerCase() === "teamgrid" || name.toLowerCase() === "team grid") return "TG";
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // SEO fallback chain: hardcoded overrides > DB meta fields > auto-generated
  const seoOverride = BLOG_SEO_OVERRIDES[blog.slug];
  const metaTitle = seoOverride
    ? seoOverride.metaTitle
    : (blog.metaTitle || `${blog.title} | TeamGrid Blog`);
  const metaDescription = seoOverride
    ? seoOverride.metaDescription
    : (blog.metaDescription || blog.description || "");
  // Normalize an admin-entered canonical: full URL stays as-is; a bare slug/path
  // gets prefixed with SITE_BASE; empty falls back to this blog's own URL.
  const resolveCanonical = (raw) => {
    const v = (raw || "").trim();
    if (!v) return `${SITE_BASE}/blog/${blog.slug}`;
    if (/^https?:\/\//i.test(v)) return v; // already a full URL (incl. external)
    const path = v.replace(/^\/+/, ""); // strip leading slashes
    // If they typed just "my-slug", treat it as /blog/my-slug; otherwise honor their path
    return `${SITE_BASE}/${path.startsWith("blog/") ? path : `blog/${path}`}`;
  };
  const canonicalUrl = seoOverride
    ? seoOverride.canonicalUrl
    : resolveCanonical(blog.canonicalUrl);

  // JSON-LD BlogPosting: hardcoded override > auto-generated from blog data
  const jsonLdBlogPosting = seoOverride
    ? seoOverride.blogPosting
    : {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "@id": `${SITE_BASE}/blog/${blog.slug}#blogposting`,
        mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_BASE}/blog/${blog.slug}` },
        headline: blog.metaTitle || blog.title,
        description: blog.metaDescription || blog.description,
        author: { "@type": "Organization", name: "TeamGrid", url: `${SITE_BASE}/` },
        publisher: { "@type": "Organization", name: "TeamGrid", url: `${SITE_BASE}/` },
        datePublished: blog.publishedAt ? new Date(blog.publishedAt).toISOString().split("T")[0] : undefined,
        dateModified: blog.updatedAt ? new Date(blog.updatedAt).toISOString().split("T")[0] : undefined,
        articleSection: blog.category || "Productivity",
        ...(blog.keywords && blog.keywords.length > 0 ? { keywords: blog.keywords } : {}),
        inLanguage: "en",
      };

  // JSON-LD FAQ: hardcoded override > auto-generated from blog.faqs
  const jsonLdFaq = seoOverride
    ? seoOverride.faq
    : (blog.faqs && blog.faqs.length > 0
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "@id": `${SITE_BASE}/blog/${blog.slug}#faq`,
            mainEntity: blog.faqs.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
          }
        : null);

  return (
    <div className="blog-container bg-white min-h-screen" style={{ fontFamily: "Inter, sans-serif" }}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={blog.featuredImage || OG_IMAGE_FALLBACK} />
        <meta property="og:site_name" content="TeamGrid" />
        {blog.publishedAt && <meta property="article:published_time" content={blog.publishedAt} />}
        {blog.author && <meta property="article:author" content={blog.author} />}
        {blog.keywords && blog.keywords.length > 0 && <meta name="keywords" content={blog.keywords.join(', ')} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={blog.featuredImage || OG_IMAGE_FALLBACK} />
        {jsonLdBlogPosting && (
          <script type="application/ld+json">
            {JSON.stringify(jsonLdBlogPosting)}
          </script>
        )}
        {jsonLdFaq && (
          <script type="application/ld+json">
            {JSON.stringify(jsonLdFaq)}
          </script>
        )}
      </Helmet>
      <PublicHeader />
      <div className="pt-16 sm:pt-18 md:pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col lg:flex-row gap-0 items-start relative">
          
          {/* Sticky sidebar - Share sidebar now lives outside the header to stick from the very top */}
          <aside
            className="hidden lg:flex lg:w-24 flex-shrink-0 justify-center pt-24"
            style={{ position: "sticky", top: "100px", alignSelf: "flex-start", zIndex: 10 }}
          >
            <div className="flex flex-col items-center gap-4">
              <span className="font-bold text-xs uppercase" style={{ color: "#A0A0A0", lineHeight: "16px" }}>
                Share
              </span>
              <div className="flex flex-col gap-2">
                {[
                  { Icon: Twitter, onClick: shareToTwitter, label: "Twitter" },
                  { Icon: Linkedin, onClick: shareToLinkedIn, label: "LinkedIn" },
                  { Icon: Facebook, onClick: shareToFacebook, label: "Facebook" },
                ].map(({ Icon, onClick, label }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={onClick}
                    className="w-10 h-10 rounded-full bg-white border flex items-center justify-center transition-colors hover:bg-gray-50"
                    style={{ borderColor: "#EDF4FF", boxShadow: "0px 1px 2px rgba(0,0,0,0.05)" }}
                    title={`Share on ${label}`}
                  >
                    <Icon className="w-4 h-4 text-[#475569]" />
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Main Column */}
          <div className="flex-1 w-full min-w-0 flex flex-col">
            {/* Article Header */}
            <header className="flex flex-col items-center justify-center py-20 lg:py-24 gap-6">
              <div className="w-full flex flex-col items-center gap-6">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <span
              className="px-3 py-1 rounded-full text-xs font-bold uppercase"
              style={{ background: "rgba(0,0,0,0.05)", color: categoryColor }}
            >
              {blog.category}
            </span>
            {/* <span className="text-[#A0A0A0] text-sm">•</span>
            <span className="text-[#475569] text-sm font-medium">{blog.readTime || "10 min read"}</span> */}
            <span className="text-[#A0A0A0] text-sm">•</span>
            <span className="text-[#475569] text-sm font-medium">{formatDate(blog.publishedAt)}</span>
          </div>
          <h1
            className="text-center font-extrabold text-black max-w-4xl"
            style={{ fontSize: "clamp(1.75rem, 4vw, 60px)", lineHeight: "68px", letterSpacing: "-1.5px" }}
          >
            {blog.title}
          </h1>
          {/* Featured image block */}
          <div
            className="w-full max-w-[976px] rounded-3xl border flex items-center justify-center overflow-hidden"
            style={{
              background: "radial-gradient(70.71% 70.71% at 50% 50%, #94A3B8 2.36%, rgba(148, 163, 184, 0) 2.36%), #FFFFFF",
              borderColor: "#EDF4FF",
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
              padding: 80,
            }}
          >
            {blog.featuredImage && blog.featuredImage.trim() !== "" && !featuredImageError ? (
              <img
                src={blog.featuredImage}
                alt={blog.title}
                className="w-full h-full object-cover"
                onError={() => setFeaturedImageError(true)}
              />
            ) : (
            <div className="flex items-center justify-center gap-8 flex-wrap">
              <div
                className="w-24 h-24 rounded-2xl flex items-center justify-center border shadow-lg"
                style={{
                  background: "#FFFFFF",
                  borderColor: "#EDF4FF",
                  boxShadow: "0px 20px 25px -5px rgba(0,0,0,0.1), 0px 8px 10px -6px rgba(0,0,0,0.1)",
                  transform: "rotate(-6deg)",
                }}
              >
                <Check className="w-12 h-12" style={{ color: TEAL }} strokeWidth={2.5} />
              </div>
              <div
                className="w-32 h-32 rounded-3xl flex items-center justify-center"
                style={{
                  background: TEAL,
                  boxShadow: "0px 0px 15px rgba(0, 209, 193, 0.3)",
                  transform: "rotate(3deg)",
                }}
              >
                <Sparkles className="w-16 h-16 text-black" strokeWidth={2.5} />
              </div>
              <div
                className="w-24 h-24 rounded-2xl flex items-center justify-center shadow-lg"
                style={{
                  background: "#000000",
                  boxShadow: "0px 20px 25px -5px rgba(0,0,0,0.1), 0px 8px 10px -6px rgba(0,0,0,0.1)",
                  transform: "rotate(12deg)",
                }}
              >
                <Lock className="w-10 h-10 text-white" strokeWidth={2} />
              </div>
            </div>
            )}
          </div>
          </div>
        </header>

        {/* Main Content gap and container between header and article */}

          {/* Article body */}
          <article className="flex-1 min-w-0 max-w-[768px] mx-auto py-8 lg:py-20 lg:px-12 flex flex-col min-h-[70vh]">
            {/* AI Summary box */}
            <div
              className="rounded-2xl border p-6 flex gap-4 mb-10"
              style={{
                background: "rgba(224, 255, 252, 0.4)",
                borderColor: "rgba(0, 209, 193, 0.2)",
              }}
            >
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: TEAL, boxShadow: "0px 1px 2px rgba(0,0,0,0.05)" }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-black">
                  <path d="M12 2l1.5 5.5L19 9l-5.5 1.5L12 16l-1.5-5.5L5 9l5.5-1.5L12 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="6" cy="18" r="1.5" fill="currentColor" />
                  <path d="M17 4h2M18 3v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <div className="flex flex-col gap-2">
                <span className="font-bold text-sm uppercase tracking-wide text-black">
                  Article Summary
                </span>
                <p className="text-[#475569]" style={{ fontSize: 18, lineHeight: "29px" }}>
                  {blog.description}
                </p>
              </div>
            </div>

            {/* Rendered content */}
            <div
              className="blog-detail-content mb-10"
              dangerouslySetInnerHTML={{ __html: blog.content || "" }}
            />

            {/* Mid-article CTA: dark banner, headline, description, two buttons */}
            <div
              className="rounded-2xl p-10 sm:p-12 flex flex-col items-center gap-5 text-center relative overflow-hidden mb-12 border"
              style={{
                background: "#0D1117",
                borderColor: "rgba(0, 209, 193, 0.25)",
                boxShadow: "0px 25px 50px -12px rgba(0, 0, 0, 0.25)",
              }}
            >
              <h3 className="relative z-10 font-bold mb-0" style={{ fontSize: 24, lineHeight: "36px", fontFamily: "Inter, sans-serif", color: "#FFFFFF" }}>
                Stop Tracking Work. Start Understanding It.
              </h3>
              <p className="relative z-10 text-white/80 max-w-[512px]" style={{ fontSize: 18, lineHeight: "32px", fontFamily: "Inter, sans-serif" }}>
                Discover how top performing teams use TeamGrid to remove bottlenecks and prevent burnout.
              </p>
              <div className="relative z-10 flex flex-wrap items-center justify-center gap-4">
                <Link
                  to="/book-demo"
                  className="px-6 py-3 rounded-xl font-bold text-white transition-colors hover:opacity-90"
                  style={{ background: TEAL }}
                >
                  See How It Works
                </Link>
                <Link
                  to="/contact-us"
                  className="px-6 py-3 rounded-xl font-bold border border-white/60 bg-transparent hover:bg-white/10 transition-colors"
                  style={{ color: "#FFFFFF" }}
                >
                  Request Demo
                </Link>
              </div>
            </div>

            {/* Author bio — avatar left, text beside it; pushed to bottom of article */}
            <div
              className="pt-10 mt-auto flex flex-row flex-nowrap items-start gap-6"
              style={{ borderTop: "1px solid #EDF4FF" }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center font-bold border-4 border-white flex-shrink-0 mt-4"
                style={{
                  background: "#000000",
                  color: "#FFFFFF",
                  boxShadow: "0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -2px rgba(0,0,0,0.1)",
                  fontSize: 20,
                  lineHeight: "28px",
                }}
              >
                {getInitials(blog.author)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm uppercase mb-1" style={{ color: "#475569", lineHeight: "32px" }}>
                  Written By
                </p>
                <p className="font-bold text-black mb-1" style={{ fontSize: 18, lineHeight: "28px" }}>
                  {blog.author}
                </p>
                <p className="text-[#475569]" style={{ fontSize: 18, lineHeight: "32px" }}>
                  Head of Product at TeamGrid. Obsessed with making work better, faster, and happier.
                </p>
              </div>
            </div>

            {/* Blog FAQs */}
            {blog.faqs && blog.faqs.length > 0 && (
              <div className="mt-16">
                <SolutionsFAQ
                  faqs={blog.faqs}
                  title="Frequently Asked Questions"
                  subtitle=""
                  defaultOpenIndex={0}
                />
              </div>
            )}
          </article>
          </div>
        </div>

        {/* Keep Exploring */}
        {relatedBlogs.length > 0 && (
          <section
            className="py-20 border-t"
            style={{ borderColor: "#EDF4FF" }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center gap-10">
              <h2 className="font-bold text-black text-center" style={{ fontSize: 24, lineHeight: "32px" }}>
                Keep Exploring
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
                {relatedBlogs.map((related) => (
                  <Link
                    key={related._id}
                    to={`/blog/${related.slug}`}
                    className="rounded-2xl border overflow-hidden bg-white transition-shadow hover:shadow-lg"
                    style={{ borderColor: "#EDF4FF" }}
                  >
                    <div
                      className="h-40 flex items-center justify-center"
                      style={{ background: "#EDF4FF" }}
                    >
                      {related.featuredImage && related.featuredImage.trim() !== "" ? (
                        <img
                          src={related.featuredImage}
                          alt={related.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg border border-[#A0A0A0]/50 flex items-center justify-center">
                          <ArrowRight className="w-5 h-5 text-[#A0A0A0]" />
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex flex-col gap-2">
                      <span
                        className="text-xs font-bold uppercase"
                        style={{ color: CATEGORY_COLORS[related.category] || TEAL }}
                      >
                        {related.category}
                      </span>
                      <h3 className="font-bold text-black line-clamp-2" style={{ fontSize: 16, lineHeight: "24px" }}>
                        {related.title}
                      </h3>
                      <span className="flex items-center gap-1 text-[#A0A0A0] text-xs">
                        Read Article
                        <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <ReadyToTransform />
        <Footer />
      </div>
    </div>
  );
};

export default BlogDetail;
